import {
    TIPO_DE_IDENTIFICACION_DIMEX,
    TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO,
    TIPO_DE_IDENTIFICACION_FISICA,
    TIPO_DE_IDENTIFICACION_JURIDICA,
    TIPO_DE_IDENTIFICACION_NITE,
    TIPO_DE_IDENTIFICACION_NO_CONTRIBUYENTE,
} from "./Constants";

export type IdentificationValidationResult = {
    valid: boolean;
    message?: string;
};

/** Tamaño maximo del campo "Numero" de identificacion en el XML de Hacienda. */
export const IDENTIFICATION_MAX_LENGTH = 20;

const ONLY_DIGITS = /^\d+$/;
const ALPHANUMERIC = /^[A-Z0-9]+$/;

/**
 * Normaliza una identificacion al formato que exige Hacienda: sin guiones ni
 * espacios y en mayusculas (la bitacora del 22/04/2026 permite caracteres
 * alfanumericos para personas juridicas).
 */
export const normalizeIdentification = (raw?: string | null): string =>
    `${raw || ""}`.replace(/[\s-]/g, "").toUpperCase();

/**
 * Valida el numero de identificacion segun la nota 4 del anexo v4.4:
 * - Cedula fisica: 9 digitos, sin cero al inicio.
 * - Cedula juridica: 10 caracteres alfanumericos (bitacora del 22/04/2026).
 * - DIMEX: 11 o 12 digitos, sin ceros al inicio.
 * - NITE: 10 digitos.
 * - Extranjero No Domiciliado / No Contribuyente: hasta 20 caracteres alfanumericos.
 */
export const validateIdentification = (
    tipo?: string | null,
    numero?: string | null
): IdentificationValidationResult => {
    const value = normalizeIdentification(numero);
    if (!value) {
        return { valid: false, message: "El numero de identificacion es requerido" };
    }
    if (value.length > IDENTIFICATION_MAX_LENGTH) {
        return { valid: false, message: `La identificacion no puede superar los ${IDENTIFICATION_MAX_LENGTH} caracteres` };
    }

    switch (`${tipo || ""}`) {
        case TIPO_DE_IDENTIFICACION_FISICA:
            if (!ONLY_DIGITS.test(value) || value.length != 9) {
                return { valid: false, message: "La cedula fisica debe tener 9 digitos, sin guiones" };
            }
            if (value.startsWith("0")) {
                return { valid: false, message: "La cedula fisica no debe iniciar con cero" };
            }
            return { valid: true };

        case TIPO_DE_IDENTIFICACION_JURIDICA:
            if (!ALPHANUMERIC.test(value) || value.length != 10) {
                return { valid: false, message: "La cedula juridica debe tener 10 caracteres, sin guiones" };
            }
            return { valid: true };

        case TIPO_DE_IDENTIFICACION_DIMEX:
            if (!ONLY_DIGITS.test(value) || (value.length != 11 && value.length != 12)) {
                return { valid: false, message: "El DIMEX debe tener 11 o 12 digitos, sin guiones" };
            }
            if (value.startsWith("0")) {
                return { valid: false, message: "El DIMEX no debe iniciar con cero" };
            }
            return { valid: true };

        case TIPO_DE_IDENTIFICACION_NITE:
            if (!ONLY_DIGITS.test(value) || value.length != 10) {
                return { valid: false, message: "El NITE debe tener 10 digitos, sin guiones" };
            }
            return { valid: true };

        case TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO:
        case TIPO_DE_IDENTIFICACION_NO_CONTRIBUYENTE:
            if (!ALPHANUMERIC.test(value)) {
                return { valid: false, message: "La identificacion solo permite numeros y letras, sin guiones" };
            }
            return { valid: true };

        default:
            // Sin tipo de identificacion no hay formato que verificar.
            return { valid: true };
    }
};

export const isValidIdentification = (tipo?: string | null, numero?: string | null): boolean =>
    validateIdentification(tipo, numero).valid;

/**
 * Orden de prueba para inferir el tipo de identificacion a partir del numero.
 * Se omite NITE ("04") a proposito: no esta en el mapa TIPOS_DE_IDENTIFICACION que
 * alimenta el select del formulario, asi que un cliente con ese tipo no se podria
 * editar despues. Los 10 digitos de un NITE caen en cedula juridica, que tambien
 * acepta 10 caracteres alfanumericos.
 */
const IDENTIFICATION_TYPE_CANDIDATES = [
    TIPO_DE_IDENTIFICACION_FISICA,
    TIPO_DE_IDENTIFICACION_JURIDICA,
    TIPO_DE_IDENTIFICACION_DIMEX,
] as const;

/**
 * Infiere el tipo de identificacion tributaria a partir del formato del numero,
 * reutilizando las reglas de `validateIdentification` para no duplicarlas:
 * 9 digitos -> Fisica, 10 alfanumericos -> Juridica, 11 o 12 digitos -> DIMEX.
 *
 * Se usa en la importacion masiva de clientes, donde los archivos externos no traen
 * una columna de tipo de identificacion.
 *
 * @returns `undefined` si el numero viene vacio (no hay nada que inferir), o
 * `TIPO_DE_IDENTIFICACION_FISICA` como valor por defecto cuando el numero no calza
 * con ningun formato conocido.
 */
export const detectIdentificationType = (numero?: string | null): string | undefined => {
    const value = normalizeIdentification(numero);
    if (!value) {
        return undefined;
    }

    const match = IDENTIFICATION_TYPE_CANDIDATES.find((tipo) => isValidIdentification(tipo, value));

    return match || TIPO_DE_IDENTIFICACION_FISICA;
};
